<template>
  <div class="match-select mt-2">
    <label :for="selectId" class="visually-hidden">{{ label }}</label>
    <select
      :id="selectId"
      class="default-select"
      :disabled="!editable"
      :value="selectedId"
      @change="onChange"
    >
      <option value="">{{ placeholder }}</option>
      <option v-for="opt in options" :key="opt.id" :value="opt.id">
        {{ plainText(opt.name) }}
      </option>
    </select>
  </div>
</template>

<script>
/*
  Keyboard and touch alternative to the drag-and-drop matching activities.

  Addresses WCAG SC 2.1.1 Keyboard (audit 27.1 - drag-and-drop was not operable by
  keyboard at all) and SC 2.5.7 Dragging Movements (audit 33.1), which requires a
  single-pointer alternative to any dragging action.

  It deliberately mutates the SAME arrays vuedraggable is bound to, in place, rather than
  keeping its own copy. Dragging and selecting therefore stay in sync automatically and the
  activity's existing save/check/reset logic keeps working untouched.
*/
let uid = 0;

export default {
  name: 'match-select',
  props: {
    // The pool of unassigned items (the activity's `list1`). Mutated in place.
    pool: { type: Array, required: true },
    // The answer slot for this question - an array holding 0 or 1 item. Mutated in place.
    answer: { type: Array, required: true },
    // Visible question text; used as the select's accessible name.
    label: { type: String, required: true },
    editable: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Choose an answer' },
  },
  data() {
    uid += 1;
    return { selectId: `match-select-${uid}` };
  },
  computed: {
    selectedId() {
      return this.answer.length ? String(this.answer[0].id) : '';
    },
    // Whatever is still unassigned, plus this question's own current answer, in the pool's
    // own (shuffled) order. Never sort by id: the activities grade by id === question
    // number, so an id-ordered list would give the answers away.
    options() {
      return this.pool.concat(this.answer);
    },
  },
  methods: {
    // Some activities put markup in the item name; a select option can only hold text.
    plainText(name) {
      return String(name).replace(/<[^>]*>/g, '');
    },
    // vue/no-mutating-props is disabled deliberately for the two array props. vuedraggable
    // mutates its own `:list` prop in place for the same reason; matching that behaviour is
    // what keeps dragging and selecting in sync without a second copy of the state.
    /* eslint-disable vue/no-mutating-props */
    onChange(event) {
      const id = event.target.value;

      // Return the current answer to the pool before assigning a new one.
      if (this.answer.length) {
        this.pool.push(this.answer[0]);
        this.answer.splice(0, this.answer.length);
      }

      if (!id) {
        this.$emit('changed', null);
        return;
      }

      const idx = this.pool.findIndex((item) => String(item.id) === id);
      if (idx === -1) return;

      const [picked] = this.pool.splice(idx, 1);
      this.answer.push(picked);
      this.$emit('changed', picked);
    },
    /* eslint-enable vue/no-mutating-props */
  },
};
</script>
